import { useState } from 'react';
import { ENGINE_TYPES, formatSpecificationLabel, SPECIFICATION_KEYS, STATUSES } from '../constants';
import { downloadXls } from '../../../utils/exportXls';
import { formatAddressLine } from '../../../utils/formatAddress';
import { resolveNamesByIds } from '../../../utils/resolveNamesByIds';
import { EXPORT_LABELS, IMPORT_LABELS } from '../../../utils/labels';
import type { Machine, MachineOption } from '../types';

interface ExportProject {
  name: string;
  year: number;
}

const EXPORT_HEADERS = [
  IMPORT_LABELS.colName,
  IMPORT_LABELS.colCategory,
  IMPORT_LABELS.colOptions,
  IMPORT_LABELS.colEngineType,
  IMPORT_LABELS.colMachineNumber,
  IMPORT_LABELS.colStatus,
  IMPORT_LABELS.colStartDate,
  IMPORT_LABELS.colStartTime,
  IMPORT_LABELS.colEndDate,
  IMPORT_LABELS.colEndTime,
  IMPORT_LABELS.colPickupOnEndDate,
  IMPORT_LABELS.colSupplierName,
  EXPORT_LABELS.colSupplierEmail,
  IMPORT_LABELS.colAddressName,
  EXPORT_LABELS.colAddress,
  EXPORT_LABELS.colProject,
  IMPORT_LABELS.colNotes,
  ...SPECIFICATION_KEYS.map(formatSpecificationLabel),
];

function formatDate(d: string | undefined): string {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

function toRows(machines: Machine[], project: ExportProject | undefined, options: MachineOption[]): string[][] {
  return machines.map((m) => {
    const managedOptionNames = resolveNamesByIds(m.option_ids ?? [], options);
    const allOptionNames = [...(m.options ?? []), ...managedOptionNames];

    return [
      m.name ?? '',
      m.category ?? '',
      allOptionNames.join(', '),
      ENGINE_TYPES[m.engine_type]?.label ?? m.engine_type,
      m.machine_number ?? '',
      STATUSES[m.status]?.label ?? m.status,
      formatDate(m.start_date),
      m.start_time ?? '',
      formatDate(m.end_date),
      m.end_time ?? '',
      m.pickup_on_end_date ? IMPORT_LABELS.pickupYes : IMPORT_LABELS.pickupNo,
      m.supplier?.name ?? '',
      m.supplier?.email ?? '',
      m.address?.name ?? '',
      m.address ? formatAddressLine(m.address) : '',
      project ? `${project.name} ${project.year}` : '',
      m.notes ?? '',
      ...SPECIFICATION_KEYS.map((k) => {
        const spec = m.specifications?.find((s) => s.key === k);
        return spec !== undefined ? String(spec.value) : '';
      }),
    ];
  });
}

export function useExportPlanningMachines(
  machines: Machine[],
  project: ExportProject | undefined,
  options: MachineOption[] = [],
) {
  const [exporting, setExporting] = useState(false);

  const exportMachines = async (): Promise<void> => {
    setExporting(true);
    try {
      await downloadXls(EXPORT_LABELS.machinesFilename, EXPORT_HEADERS, toRows(machines, project, options));
    } finally {
      setExporting(false);
    }
  };

  return { exportMachines, exporting };
}
